import React, { useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SourceFilesMenu from "../../../components/common/SourceFilesMenu";
import SalesOrderInboundMessagesPageGen from "./generated/SalesOrderInboundMessagesPageGen";
import salesOrderInboundMessagesService from "../../../services/legacy/salesOrderInboundMessagesService";

const SHOW_IN_GRID = new Set(["dataAreaId", "iSalesId", "salesId", "sourcepdffilename", "sourcelinenum", "messagetype", "actiontype", "processingstatus", "actionsummary", "emailfrom", "emaildate", "executedby", "executedat"]);
const SHOW_IN_FILTER = new Set(["iSalesId", "salesId", "sourcepdffilename", "messagetype", "actiontype", "processingstatus", "emailfrom"]);
const PENDING_STATUSES = new Set(["pending_manual_review", "ready_to_execute"]);

// Row-color typology for the inbound-message log: distinguishes actions on
// already-existing orders from the newer categories logged by processor.py
// (new order created, order line skipped, whole email/PDF skipped or failed).
const MESSAGE_CATEGORIES = {
  newOrder: {
    color: "#2e7d32",
    background: "#e6f4ea",
    labelKey: "categoryNewOrder",
    tooltipKey: "categoryNewOrderTooltip",
  },
  existingOrderActionPending: {
    color: "#8a6100",
    background: "#fff4db",
    labelKey: "categoryExistingOrderActionPending",
    tooltipKey: "categoryExistingOrderActionPendingTooltip",
  },
  existingOrderAction: {
    color: "#1d4ed8",
    background: "#e3f0ff",
    labelKey: "categoryExistingOrderAction",
    tooltipKey: "categoryExistingOrderActionTooltip",
  },
  lineSkipped: {
    color: "#c2660b",
    background: "#fff1e0",
    labelKey: "categoryLineSkipped",
    tooltipKey: "categoryLineSkippedTooltip",
  },
  documentIssue: {
    color: "#b91c1c",
    background: "#fdecea",
    labelKey: "categoryDocumentIssue",
    tooltipKey: "categoryDocumentIssueTooltip",
  },
};

const categorizeInboundMessage = (row) => {
  const messageType = String(row?.messagetype || "").trim().toLowerCase();
  const status = String(row?.processingstatus || "").trim().toLowerCase();
  if (messageType === "order_processing") {
    return MESSAGE_CATEGORIES.newOrder;
  }
  if (messageType === "line_processing") {
    return MESSAGE_CATEGORIES.lineSkipped;
  }
  if (messageType === "document_processing") {
    return MESSAGE_CATEGORIES.documentIssue;
  }
  if (messageType === "action_request") {
    return PENDING_STATUSES.has(status)
      ? MESSAGE_CATEGORIES.existingOrderActionPending
      : MESSAGE_CATEGORIES.existingOrderAction;
  }
  return null;
};

const transformColumns = (baseColumns) => {
  return baseColumns.map((column) => ({
    ...column,
    showInGrid: SHOW_IN_GRID.has(column.key),
    showInFilter: SHOW_IN_FILTER.has(column.key),
  }));
};

const saveBlob = ({ blob, filename }) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const openBlob = ({ blob }) => {
  const url = window.URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

const SourceArtifactButtons = ({ formData, isEditing }) => {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(null);
  const id = formData?.id;

  if (!isEditing || !id) {
    return null;
  }

  const handleDownload = async (kind) => {
    setDownloading(kind);
    try {
      if (kind === "pdf") {
        saveBlob(await salesOrderInboundMessagesService.downloadSourcePdf(id));
      } else if (kind === "eml") {
        openBlob(await salesOrderInboundMessagesService.downloadSourceEmail(id));
      } else if (kind === "json") {
        saveBlob(await salesOrderInboundMessagesService.downloadSourceJson(id));
      }
    } catch (error) {
      window.alert(error?.message || t("salesOrderInboundMessages.sourceFileUnavailable"));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div
      className="sales-order-inbound-source-actions"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        padding: "0 0 16px",
        borderBottom: "1px solid #e5e7eb",
        marginBottom: 18,
      }}
    >
      <SourceFilesMenu
        availability={{
          pdf: Boolean(formData?.sourcepdffilename),
          eml: Boolean(formData?.sourceemailfilename),
          json: Boolean(formData?.sourcejsonfilename),
        }}
        loadingKind={downloading}
        onSelect={handleDownload}
      />
    </div>
  );
};

const CategoryLegend = () => {
  const { t } = useTranslation();
  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 10,
        padding: "10px 0 0",
        fontSize: 12,
      }}
    >
      <span style={{ fontWeight: 600, color: "#555" }}>
        {t("salesOrderInboundMessages.categoryLegend")}:
      </span>
      {Object.values(MESSAGE_CATEGORIES).map((category) => (
        <span
          key={category.labelKey}
          title={t(`salesOrderInboundMessages.${category.tooltipKey}`)}
          style={{
            display: "inline-block",
            padding: "2px 10px",
            borderRadius: 12,
            backgroundColor: category.background,
            color: category.color,
            cursor: "default",
          }}
        >
          {t(`salesOrderInboundMessages.${category.labelKey}`)}
        </span>
      ))}
    </div>
  );
};

const SalesOrderInboundMessagesPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const service = salesOrderInboundMessagesService;
  const initialFilters = useMemo(() => {
    const fromState = location?.state?.initialFilters || {};
    const query = new URLSearchParams(location?.search || "");
    const fromQuery = {};
    const dataAreaId = (query.get("dataAreaId") || "").trim();
    const iSalesId = (query.get("iSalesId") || "").trim();
    const salesId = (query.get("salesId") || "").trim();
    if (dataAreaId) fromQuery.dataAreaId = dataAreaId;
    if (iSalesId) fromQuery.iSalesId = iSalesId;
    if (salesId) fromQuery.salesId = salesId;
    return { ...fromState, ...fromQuery };
  }, [location?.state, location?.search]);

  const resolveRowStyle = (row) => {
    const category = categorizeInboundMessage(row);
    if (!category) {
      return null;
    }
    const tooltipParts = [t(`salesOrderInboundMessages.${category.tooltipKey}`)];
    const actionType = String(row?.actiontype || "").trim();
    if (actionType) {
      tooltipParts.push(
        t(`salesOrderInboundMessages.actionTypeLabels.${actionType}`, { defaultValue: actionType })
      );
    }
    const reason = String(row?.aiparsereason || "").trim();
    if (reason) {
      tooltipParts.push(reason);
    }
    return {
      style: { backgroundColor: category.background },
      title: tooltipParts.join(" — "),
    };
  };

  const renderForm = ({ formData, isEditing, renderDefaultForm }) => (
    <>
      <SourceArtifactButtons formData={formData} isEditing={isEditing} />
      {renderDefaultForm()}
    </>
  );

  return (
    <>
      <CategoryLegend />
      <SalesOrderInboundMessagesPageGen
        key={`soim:${initialFilters.dataAreaId || ""}:${initialFilters.iSalesId || ""}:${initialFilters.salesId || ""}:${location?.search || ""}`}
        service={service}
        transformColumns={transformColumns}
        crudConfigOverride={{
          initialFilters,
          gridRowStyleResolver: resolveRowStyle,
          renderForm,
        }}
      />
    </>
  );
};

export default SalesOrderInboundMessagesPage;
